import { ReactNode } from 'react';
import { Item } from '@radix-ui/react-dropdown-menu';

import { cn } from 'styles';

import { IconProps } from '../Icons';
import { RouteProps, Link } from '../Link';

import { ShortcutTag } from '../UI';

type LinkProps = RouteProps & {
  linkTarget?: string;
  onClick?: never;
  href?: never;
  closeOnClick?: never;
};

type ActionProps =
  | LinkProps
  | {
      to?: never;
      linkTarget?: string;
      href: string;
      onClick?: never;
      params?: never;
      closeOnClick?: never;
    }
  | {
      onClick?: () => void;
      params?: never;
      linkTarget?: never;
      href?: never;
      to?: never;
      closeOnClick?: boolean;
    };

export type DropdownMenuItemProps = ActionProps & {
  children: ReactNode;
  className?: string;

  inset?: boolean;

  disabled?: boolean;
  onClick?: () => void;

  shortCut?: string | string[];

  /**
   * A Icon component to be rendered on the left side
   *
   * Pass in the component if you want to use the standard size and layout of the icon
   */
  Icon?: (props: IconProps) => JSX.Element;
};

export function DropdownMenuItem({
  children,
  disabled,
  onClick,
  inset,
  className,
  shortCut,
  Icon,
  linkTarget,
  closeOnClick = true,
  to,
  href,
  ...linkParams
}: DropdownMenuItemProps): JSX.Element {
  const classes = cn(
    'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
    'focus:bg-accent focus:text-accent-foreground',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    inset && 'pl-8',
    className,
  );

  const child = (
    <>
      {Icon && <Icon size={16} />}

      {children}

      {shortCut && <ShortcutTag keys={shortCut} />}
    </>
  );

  const isLink = !!href || !!to;

  if (!isLink)
    return (
      <Item
        className={classes}
        disabled={disabled}
        onSelect={(e) => {
          if (!closeOnClick) e.preventDefault();
          onClick?.();
        }}
      >
        {child}
      </Item>
    );

  const linkProps = {
    to: to || href,
    ...linkParams,
  } as RouteProps;

  return (
    <Item disabled={disabled} asChild>
      <Link target={linkTarget} {...linkProps} className={classes}>
        {child}
      </Link>
    </Item>
  );
}
